import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, LayoutDashboard, ShieldCheck, Menu, X, ArrowRight } from "lucide-react";
import { OFFICIAL_LOGO } from "@/lib/brand";
import api from "@/lib/api";

// Time-based restrictions have been replaced by the global registration toggle

export default function Navbar() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isLanding = pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!isLanding) return;
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isLanding]);

  const [registrationOpen, setRegistrationOpen] = useState(false);
  useEffect(() => {
    api.get("/meta/events").then(({ data }) => setRegistrationOpen(data.registration_open)).catch(() => {});
  }, []);

  // Mobile-only sticky register bar: the desktop nav's Register link is
  // hidden below md, so guests scrolling past the hero had no visible way
  // to register until they reached the page's own CTA section (or opened
  // the hamburger). Appears once they've scrolled past the fold, on any
  // page — not just Landing — since the same gap exists everywhere.
  const [showMobileRegister, setShowMobileRegister] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowMobileRegister(window.scrollY > 480);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const mobileRegisterVisible = !user && registrationOpen && pathname !== "/register" && showMobileRegister;

  const transparent = isLanding && !scrolled;
  let bgClass;
  if (transparent) {
    bgClass = "bg-transparent border-transparent";
  } else if (isLanding) {
    bgClass = "bg-[#0B1429]/85 backdrop-blur-xl border-white/10";
  } else {
    bgClass = "bg-[#0B1429]/95 backdrop-blur-xl border-white/10";
  }

  const dashboardHref = (() => {
    if (!user) return "/dashboard";
    if (user.role === "admin") return "/admin";
    if (user.role === "logistics_team") return "/team/logistics";
    if (user.role === "accommodation_team") return "/team/accommodation";
    if (user.role === "security_team") return "/team/security";
    if (user.role === "delegate") return "/team/delegate";
    return "/dashboard";
  })();

  // Cross-page anchor navigation: clicking /#about from anywhere goes to landing+scroll
  const goAnchor = (id) => {
    setMenuOpen(false);
    if (isLanding) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 350);
    }
  };

  // Handle URL hash on page load
  useEffect(() => {
    if (isLanding && window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 400);
    }
  }, [isLanding]);

  return (
    <>
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${bgClass}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 shrink-0 group" onClick={() => window.scrollTo(0, 0)}>
          <div className="bg-white rounded-lg p-1 shadow-[0_0_24px_rgba(0,240,255,0.15)] transition-transform group-hover:scale-105">
            <img src={OFFICIAL_LOGO} alt="2nd Asian Masters Swimming Championships 2026" className="h-12 w-12 object-contain" />
          </div>
          <div className="leading-tight hidden sm:block">
            <div className="font-display font-black text-xs tracking-[0.18em] text-[#00F0FF]">2ND ASIAN MASTERS</div>
            <div className="font-display font-bold text-sm tracking-wider text-white">SWIMMING · 2026</div>
            <div className="text-[9px] text-[#94A3B8] uppercase tracking-[0.2em] mt-0.5">Bengaluru · India</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-5 lg:gap-7 text-sm">
          <button onClick={() => goAnchor("about")} className="text-white/85 hover:text-[#00F0FF] transition" data-testid="nav-about">About</button>
          <Link
            to="/results"
            className="hover:text-[#00F0FF] transition"
            data-testid="nav-results"
          >
            Results
          </Link>
          <Link to="/heat-list" onClick={() => window.scrollTo(0, 0)} className="text-white/85 hover:text-[#00F0FF] transition" data-testid="nav-heat-list">Heat List</Link>
          <button onClick={() => goAnchor("venue")} className="text-white/85 hover:text-[#00F0FF] transition" data-testid="nav-venue">Venue</button>
          <Link to="/city-guide" onClick={() => window.scrollTo(0, 0)} className="text-white/85 hover:text-[#00F0FF] transition" data-testid="nav-city">Bengaluru</Link>
          <button onClick={() => goAnchor("fees")} className="text-white/85 hover:text-[#00F0FF] transition" data-testid="nav-fees">Fees</button>
          <button onClick={() => goAnchor("faq")} className="text-white/85 hover:text-[#00F0FF] transition" data-testid="nav-faq">FAQ</button>
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="admin-link" className="hidden md:inline-flex items-center gap-2 text-[#00F0FF] text-sm font-semibold">
                  <ShieldCheck className="w-4 h-4" /> Admin
                </Link>
              )}
              <Link
                to={dashboardHref}
                data-testid="dashboard-link"
                className="hidden md:inline-flex items-center gap-2 text-white/85 hover:text-[#00F0FF] text-sm"
              >
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn" className="hidden md:inline-flex text-[#FF6B6B] hover:text-white text-sm items-center gap-1">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="hidden md:inline text-white/85 hover:text-[#00F0FF] text-sm">Login</Link>
              {registrationOpen && (
                <Link to="/register" data-testid="nav-register" className="hidden md:inline-flex btn-primary !py-2.5 !px-5 text-xs">Register</Link>
              )}
            </>
          )}
          {/* Mobile menu toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            className="md:hidden w-11 h-11 flex items-center justify-center rounded border border-white/15 text-white touch-manipulation"
            data-testid="mobile-menu-toggle"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div id="mobile-menu" className="md:hidden bg-[#0B1429]/97 backdrop-blur-xl border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1 text-sm">
            <button onClick={() => goAnchor("about")} className="text-left min-h-[44px] flex items-center text-white/85 hover:text-[#00F0FF] border-b border-white/5 touch-manipulation">About</button>
            <Link to="/results" onClick={() => { setMenuOpen(false); window.scrollTo(0, 0); }} className="min-h-[44px] flex items-center text-white/85 hover:text-[#00F0FF] border-b border-white/5 touch-manipulation">Results</Link>
            <Link to="/heat-list" onClick={() => { setMenuOpen(false); window.scrollTo(0, 0); }} className="min-h-[44px] flex items-center text-white/85 hover:text-[#00F0FF] border-b border-white/5 touch-manipulation">Heat List</Link>
            <button onClick={() => goAnchor("venue")} className="text-left min-h-[44px] flex items-center text-white/85 hover:text-[#00F0FF] border-b border-white/5 touch-manipulation">Venue</button>
            <Link to="/city-guide" onClick={() => { setMenuOpen(false); window.scrollTo(0, 0); }} className="min-h-[44px] flex items-center text-white/85 hover:text-[#00F0FF] border-b border-white/5 touch-manipulation">Bengaluru</Link>
            <button onClick={() => goAnchor("fees")} className="text-left min-h-[44px] flex items-center text-white/85 hover:text-[#00F0FF] border-b border-white/5 touch-manipulation">Fees</button>
            <button onClick={() => goAnchor("faq")} className="text-left min-h-[44px] flex items-center text-white/85 hover:text-[#00F0FF] border-b border-white/5 touch-manipulation">FAQ</button>
            {user ? (
              <>
                <Link to={dashboardHref} onClick={() => setMenuOpen(false)} className="min-h-[44px] flex items-center text-[#00F0FF] touch-manipulation">Dashboard →</Link>
                {user.role === "admin" && <Link to="/admin" onClick={() => setMenuOpen(false)} className="min-h-[44px] flex items-center text-[#00F0FF] touch-manipulation">Admin →</Link>}
                <button onClick={() => { logout(); setMenuOpen(false); navigate("/login"); }} className="text-left min-h-[44px] flex items-center text-[#FF6B6B] touch-manipulation">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMenuOpen(false)} className="min-h-[44px] flex items-center text-white/85 touch-manipulation">Login</Link>
                {registrationOpen && (
                  <Link to="/register" onClick={() => setMenuOpen(false)} className="btn-primary !py-2.5 !px-5 text-xs justify-center text-center mt-2 touch-manipulation">Register</Link>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </header>

    {/* Mobile-only sticky register bar — thumb-zone CTA for guests scrolled
        past the fold, where the desktop nav's Register link is hidden. */}
    {mobileRegisterVisible && (
      <div
        data-testid="mobile-sticky-register"
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#0B1429]/95 backdrop-blur-xl border-t border-white/10"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="px-4 py-3">
          <Link
            to="/register"
            data-testid="mobile-sticky-register-cta"
            className="btn-primary w-full justify-center min-h-[44px] touch-manipulation"
          >
            Register as Athlete <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    )}
    </>
  );
}
